import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import type { Job } from "@/lib/mockData";
import { StatusPill } from "@/components/ui/StatusPill";
import { jobStatusLabel } from "@/lib/jobStatus";
import { WaveMark } from "@/components/ui/WaveMark";
import { Kbd } from "@/components/ui/Kbd";
import { fmtElapsed } from "@/lib/format";
import { modKey } from "@/lib/platform";

interface Props {
  approvals: Job[];
  takeovers: Job[];
}

export function AttentionRail({ approvals, takeovers }: Props) {
  return (
    <aside
      data-tour="attention-rail"
      className="flex w-[340px] shrink-0 flex-col gap-4 border-l border-paper-line bg-paper-1 p-4"
    >
      <header className="flex items-baseline justify-between">
        <h2 className="text-md font-medium text-ink-1">Needs Attention</h2>
        <span className="font-mono text-xs text-ink-4">
          {approvals.length + takeovers.length}
        </span>
      </header>

      {approvals.length + takeovers.length === 0 && (
        <div className="rounded-md border border-dashed border-paper-line bg-paper-2 p-6 text-center text-sm text-ink-3">
          Inbox zero. Nice.
        </div>
      )}

      {approvals.length > 0 && (
        <Group
          title="Approvals"
          count={approvals.length}
          items={approvals}
          hrefBase="/approvals"
        />
      )}
      {takeovers.length > 0 && (
        // Jobs that asked for a person (a CAPTCHA, an unfamiliar page) and
        // jobs a supervisor is controlling. Each opens the job screen,
        // where the live view and Take control are.
        <Group title="Needs a person" count={takeovers.length} items={takeovers} hrefBase="/jobs" />
      )}

      <AskFlowCard />
    </aside>
  );
}

function Group({
  title,
  count,
  items,
  hrefBase,
}: {
  title: string;
  count: number;
  items: Job[];
  hrefBase: string;
}) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-ink-2">{title}</h3>
        <span className="font-mono text-xs text-ink-4">{count}</span>
      </div>
      <ul className="space-y-2">
        {items.slice(0, 4).map((job) => (
          <li key={job.id}>
            <Link
              to={hrefBase === "/approvals" ? "/approvals" : `/jobs/${job.id}`}
              className="block rounded-md border border-paper-line bg-paper-2 p-3 transition-colors hover:bg-paper-tint"
            >
              <div className="flex items-center justify-between gap-2">
                <StatusPill status={job.status} label={jobStatusLabel(job.status, job.apiStatus)} />
                <span className="font-mono text-[11px] text-ink-4">{job.id}</span>
              </div>
              <div className="mt-1.5 truncate text-sm text-ink-1">{job.workflow.name}</div>
              <div className="mt-0.5 truncate text-xs text-ink-3">
                {job.patient ? `${job.patient.name} · ` : ""}
                {job.entity.name} · {fmtElapsed(job.elapsed)}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AskFlowCard() {
  return (
    <div
      className="rounded-md border border-teal-100 p-4 text-sm"
      style={{
        background: "linear-gradient(180deg, var(--teal-50), var(--paper-2))",
      }}
    >
      <div className="flex items-center gap-2 text-teal-700">
        <WaveMark size={22} />
        <span className="font-medium">Ask Flow</span>
        <span className="ml-auto flex items-center gap-1 text-[11px] text-ink-3">
          <Kbd>{modKey}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </div>
      <p className="mt-2 text-xs text-ink-3">
        Natural-language commands to your fleet. Try:
      </p>
      <ul className="mt-2 space-y-1 text-xs text-ink-2">
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-teal-500" aria-hidden />
          "What's running right now?"
        </li>
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-teal-500" aria-hidden />
          "Pause all SpineOne jobs."
        </li>
        <li className="flex items-center gap-1.5">
          <Sparkles size={10} className="text-teal-500" aria-hidden />
          "Why did J-28441 fail?"
        </li>
      </ul>
    </div>
  );
}
